<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="carouselWrap"
    @mouseenter="onPointerEnter"
    @mouseleave="onPointerLeave"
  >
    <template v-if="pics.length">
      <!-- legacy：与现网完全一致 -->
      <template v-if="isLegacy">
        <img
          v-for="(p, i) in pics"
          :key="picKey(p, i)"
          class="carouselImage"
          :class="{ active: i === currentIndex }"
          :src="picUrl(p)"
          :style="imgFitStyle"
          alt=""
        />
        <div v-if="pics.length > 1" class="carousel-dots carousel-dots--legacy">
          <span
            v-for="(p, i) in pics"
            :key="'dot-' + picKey(p, i)"
            :class="{ active: i === currentIndex }"
          />
        </div>
        <span v-if="pics.length" class="preview-media-badge">{{ currentIndex + 1 }}/{{ pics.length }}</span>
      </template>

      <!-- 样式1：封面流 -->
      <template v-else-if="visualStyle === 'coverFlow'">
        <div
          class="cover-flow-stage"
          :class="[
            layoutDirection === 'vertical' ? 'cover-flow-stage--vertical' : 'cover-flow-stage--horizontal',
          ]"
        >
          <div
            v-for="slot in coverFlowSlots"
            :key="'cf-' + slot.offset"
            class="cover-flow-item"
            :class="coverFlowItemClass(slot.offset)"
            :style="coverFlowItemStyle(slot.offset)"
            @click="onCoverFlowClick(slot.offset)"
          >
            <div class="cover-flow-frame" :style="coverFlowBorderStyle(slot.offset)">
              <img
                class="cover-flow-img"
                :src="picUrl(pics[slot.index])"
                :style="imgFitStyle"
                alt=""
              />
            </div>
          </div>
        </div>
        <div
          v-if="showNavigationArrows"
          class="carousel-arrows"
          :class="arrowWrapClass"
        >
          <button
            type="button"
            class="carousel-arrow-btn"
            :style="arrowBtnStyle"
            @click.stop="go(-1)"
            aria-label="prev"
          >
            <i :class="layoutDirection === 'vertical' ? 'el-icon-arrow-up' : 'el-icon-arrow-left'" />
          </button>
          <button
            type="button"
            class="carousel-arrow-btn"
            :style="arrowBtnStyle"
            @click.stop="go(1)"
            aria-label="next"
          >
            <i :class="layoutDirection === 'vertical' ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
          </button>
        </div>
        <span v-if="showIndexBadge" class="preview-media-badge">{{ currentIndex + 1 }}/{{ pics.length }}</span>
      </template>

      <!-- 样式2 / 样式3：全幅淡入 + 圆点/箭头 -->
      <template v-else>
        <img
          v-for="(p, i) in pics"
          :key="picKey(p, i)"
          class="carouselImage"
          :class="{ active: i === currentIndex }"
          :src="picUrl(p)"
          :style="[imgFitStyle, fadeTransitionStyle]"
          alt=""
        />
        <div
          v-if="showDots"
          class="carousel-dots"
          :class="[dotsPositionClass, layoutDirection === 'vertical' && 'carousel-dots--vertical']"
          :style="{ pointerEvents: dotsClickable ? 'auto' : 'none' }"
        >
          <span
            v-for="(p, i) in pics"
            :key="'dot-' + picKey(p, i)"
            :class="{ active: i === currentIndex, 'dot--fade': visualStyle === 'fadeDots' }"
            @click="onDotClick(i)"
          />
        </div>
        <div
          v-if="showNavigationArrows"
          class="carousel-arrows"
          :class="arrowWrapClass"
        >
          <button
            type="button"
            class="carousel-arrow-btn"
            :style="arrowBtnStyle"
            @click.stop="go(-1)"
            aria-label="prev"
          >
            <i :class="layoutDirection === 'vertical' ? 'el-icon-arrow-up' : 'el-icon-arrow-left'" />
          </button>
          <button
            type="button"
            class="carousel-arrow-btn"
            :style="arrowBtnStyle"
            @click.stop="go(1)"
            aria-label="next"
          >
            <i :class="layoutDirection === 'vertical' ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
          </button>
        </div>
        <span v-if="showIndexBadge" class="preview-media-badge">{{ currentIndex + 1 }}/{{ pics.length }}</span>
      </template>
    </template>
    <div v-else class="carousel-placeholder">
      <i class="iconfont icon-lunbotu" />
      <span>{{ $tt("多图轮播") }}</span>
    </div>
  </div>
</template>

<script>
import previewMediaMixin from "@/utils/previewMediaMixin";
import {
  getCarouselLayout,
  getCarouselPlayMode,
  getCarouselVisualStyle,
  isLegacyCarousel,
  mergeArrows,
  mergeCoverFlow,
  mergeFadeDots,
  mergeTransition,
  shouldAutoplay,
} from "@/utils/carouselExt";

export default {
  mixins: [previewMediaMixin],
  props: {
    element: {
      type: Object,
      default: () => {},
    },
    isPreview: Boolean,
  },
  data() {
    return {
      currentIndex: 0,
      autoTimer: null,
      hoverPaused: false,
    };
  },
  computed: {
    style() {
      return (this.element && this.element.style) || {};
    },
    pics() {
      return this.style.carouselPics || [];
    },
    intervalSec() {
      return Number(this.style.interval) || 3;
    },
    isLegacy() {
      return isLegacyCarousel(this.style);
    },
    visualStyle() {
      return getCarouselVisualStyle(this.style);
    },
    playMode() {
      return getCarouselPlayMode(this.style);
    },
    layoutDirection() {
      return getCarouselLayout(this.style);
    },
    fadeDotsConfig() {
      return mergeFadeDots(this.style);
    },
    coverFlowConfig() {
      return mergeCoverFlow(this.style);
    },
    arrowsConfig() {
      return mergeArrows(this.style);
    },
    transitionConfig() {
      return mergeTransition(this.style);
    },
    fadeTransitionStyle() {
      if (this.isLegacy) {
        return {};
      }
      const ms = this.transitionConfig.durationMs;
      return { transition: `opacity ${ms}ms ease` };
    },
    imgFitStyle() {
      const size = this.style.backgroundSize || "contain";
      if (size === "100% 100%") {
        return { objectFit: "fill" };
      }
      if (size === "cover") {
        return { objectFit: "cover" };
      }
      return { objectFit: "contain" };
    },
    showIndexBadge() {
      if (this.isLegacy) {
        return this.pics.length > 0;
      }
      if (this.visualStyle === "fadeDots") {
        return !!this.fadeDotsConfig.showIndexBadge;
      }
      return this.pics.length > 0;
    },
    showDots() {
      if (this.visualStyle !== "fadeDots" || this.pics.length < 2) {
        return false;
      }
      return true;
    },
    dotsClickable() {
      if (this.isLegacy) {
        return false;
      }
      return !!this.fadeDotsConfig.dotsClickable;
    },
    dotsPositionClass() {
      const pos = this.fadeDotsConfig.dotsPosition || "bottomLeft";
      if (pos === "bottomCenter") {
        return "carousel-dots--center";
      }
      return "carousel-dots--bottom-left";
    },
    /** 样式1 封面流、样式2 圆点、样式3：半透明导航箭头 */
    showNavigationArrows() {
      if (this.pics.length < 2) {
        return false;
      }
      const withArrows = ["coverFlow", "fadeDots", "arrows"];
      if (!withArrows.includes(this.visualStyle)) {
        return false;
      }
      // 画布编辑态始终展示，便于所见即所得
      if (!this.isPreview) {
        return true;
      }
      if (this.playMode === "manual" || this.playMode === "autoAndManual") {
        return true;
      }
      if (this.visualStyle === "coverFlow" || this.visualStyle === "fadeDots") {
        return this.playMode === "auto";
      }
      if (this.playMode === "auto") {
        return !!this.arrowsConfig.showInAuto;
      }
      return false;
    },
    coverFlowHalf() {
      const n = Number(this.coverFlowConfig.visibleCount) || 3;
      return Math.max(1, Math.floor(n / 2));
    },
    coverFlowSlots() {
      const n = this.pics.length;
      if (!n) {
        return [];
      }
      const half = this.coverFlowHalf;
      const slots = [];
      for (let offset = -half; offset <= half; offset += 1) {
        const index = (this.currentIndex + offset + n * 10) % n;
        slots.push({ offset, index });
      }
      return slots;
    },
    arrowBtnStyle() {
      const size = Number(this.arrowsConfig.size) || 22;
      return {
        width: `${size}px`,
        height: `${size}px`,
        color: this.arrowsConfig.color || "rgba(120, 120, 120, 0.55)",
      };
    },
    /** 样式3：横版左右居中；竖版上下居中（与布局方向一致） */
    arrowWrapClass() {
      if (this.layoutDirection === "vertical") {
        return "carousel-arrows--middleVertical";
      }
      if (this.arrowsConfig.position === "bottomCenter") {
        return "carousel-arrows--bottomCenter";
      }
      return "carousel-arrows--middleSides";
    },
  },
  watch: {
    pics: {
      deep: true,
      handler() {
        this.clampIndex();
        this.startAutoplay();
      },
    },
    intervalSec() {
      this.startAutoplay();
    },
    playMode() {
      this.startAutoplay();
    },
    visualStyle() {
      this.startAutoplay();
    },
    hoverPaused() {
      this.startAutoplay();
    },
    "element.style.carouselExt": {
      deep: true,
      handler() {
        this.clampIndex();
        this.startAutoplay();
      },
    },
  },
  mounted() {
    this.startAutoplay();
  },
  beforeDestroy() {
    this.stopAutoplay();
  },
  methods: {
    picUrl(item) {
      if (!item) {
        return "";
      }
      return item.file_down_path || item.fileDownPath || item.url || "";
    },
    picKey(item, i) {
      return (item && (item.id || this.picUrl(item))) || i;
    },
    clampIndex() {
      if (!this.pics.length) {
        this.currentIndex = 0;
        return;
      }
      if (this.currentIndex >= this.pics.length) {
        this.currentIndex = 0;
      }
    },
    autoplayBlocked() {
      if (this.hoverPaused && this.playMode === "autoAndManual" && this.fadeDotsConfig.pauseOnHover) {
        return true;
      }
      return false;
    },
    startAutoplay() {
      this.stopAutoplay();
      if (!shouldAutoplay(this.style, this.pics.length)) {
        return;
      }
      if (this.autoplayBlocked()) {
        return;
      }
      const sec = Math.max(0.5, this.intervalSec);
      this.autoTimer = setInterval(() => {
        if (this.autoplayBlocked()) {
          return;
        }
        this.go(1);
      }, sec * 1000);
    },
    stopAutoplay() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    },
    go(dir) {
      const n = this.pics.length;
      if (!n) {
        return;
      }
      this.currentIndex = (this.currentIndex + dir + n) % n;
    },
    jumpTo(i) {
      const n = this.pics.length;
      if (!n || i < 0 || i >= n) {
        return;
      }
      this.currentIndex = i;
    },
    onDotClick(i) {
      if (!this.dotsClickable) {
        return;
      }
      this.jumpTo(i);
    },
    onCoverFlowClick(offset) {
      if (offset === 0) {
        return;
      }
      this.go(offset > 0 ? 1 : -1);
    },
    onPointerEnter() {
      if (
        this.visualStyle === "fadeDots" &&
        this.playMode === "autoAndManual" &&
        this.fadeDotsConfig.pauseOnHover
      ) {
        this.hoverPaused = true;
        this.stopAutoplay();
      }
    },
    onPointerLeave() {
      if (this.hoverPaused) {
        this.hoverPaused = false;
        this.startAutoplay();
      }
    },
    coverFlowItemClass(offset) {
      if (offset === 0) {
        return "cover-flow-item--center";
      }
      if (Math.abs(offset) === 1) {
        return "cover-flow-item--side";
      }
      return "cover-flow-item--far";
    },
    coverFlowItemStyle(offset) {
      const cfg = this.coverFlowConfig;
      const scale =
        offset === 0 ? Number(cfg.scaleCenter) || 1 : Number(cfg.scaleSide) || 0.72;
      const isVert = this.layoutDirection === "vertical";
      const gap = 8;
      const translate = offset * (isVert ? 38 : 42);
      if (isVert) {
        return {
          transform: `translateY(${translate}%) scale(${scale})`,
          zIndex: 10 - Math.abs(offset),
        };
      }
      return {
        transform: `translateX(${translate}%) scale(${scale})`,
        zIndex: 10 - Math.abs(offset),
      };
    },
    coverFlowBorderStyle(offset) {
      const cfg = this.coverFlowConfig;
      const abs = Math.abs(offset);
      let borderColor = "transparent";
      let borderWidth = "0";
      let boxShadow = `0 0 8px ${cfg.frameGlowColor || "#00D4FF"} inset`;
      if (offset === 0) {
        borderColor = cfg.centerBorderColor || "#FFB020";
        borderWidth = "3px";
      } else if (abs === 1) {
        borderColor = cfg.sideBorderColor || "#E02020";
        borderWidth = "3px";
      } else {
        borderColor = cfg.frameGlowColor || "#00D4FF";
        borderWidth = "1px";
      }
      return {
        borderColor,
        borderWidth,
        borderStyle: "solid",
        boxShadow,
      };
    },
    handlePreviewMediaEvent(event) {
      if (event === "prev") {
        this.go(-1);
      } else if (event === "next") {
        this.go(1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.carouselWrap {
  width: 100%;
  height: 100%;
  user-select: none;
  display: flex;
  color: #000;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: #f4f6f8;
}
.carouselImage {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.45s ease;
  pointer-events: none;
  &.active {
    opacity: 1;
  }
}
.carousel-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  .iconfont {
    font-size: 40px;
    margin-bottom: 6px;
  }
  span {
    font-size: 12px;
  }
}
.carousel-dots {
  position: absolute;
  display: flex;
  gap: 6px;
  z-index: 2;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.45);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
    cursor: default;
    &.active {
      width: 14px;
      border-radius: 4px;
      background: #409eff;
    }
    &.dot--fade {
      background: rgba(255, 200, 200, 0.85);
      &.active {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #e02020;
      }
    }
  }
}
.carousel-dots--legacy {
  left: 0;
  right: 0;
  bottom: 10px;
  justify-content: center;
  pointer-events: none;
}
.carousel-dots--center {
  left: 0;
  right: 0;
  bottom: 10px;
  justify-content: center;
}
.carousel-dots--bottom-left {
  left: 10px;
  bottom: 10px;
  flex-direction: row;
}
.carousel-dots--vertical.carousel-dots--bottom-left {
  flex-direction: column;
}
.preview-media-badge {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  pointer-events: none;
  z-index: 2;
}
.cover-flow-stage {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 900px;
}
.cover-flow-stage--horizontal {
  flex-direction: row;
}
.cover-flow-stage--vertical {
  flex-direction: column;
}
.cover-flow-item {
  position: absolute;
  width: 62%;
  height: 72%;
  transition: transform 0.45s ease, opacity 0.35s ease;
  cursor: pointer;
  z-index: 1;
}
.cover-flow-item--far {
  opacity: 0.55;
}
.cover-flow-frame {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: 2px;
  overflow: hidden;
  background: rgba(0, 20, 40, 0.35);
}
.cover-flow-img {
  width: 100%;
  height: 100%;
  display: block;
}
.carousel-arrows {
  position: absolute;
  z-index: 20;
  display: flex;
  gap: 12px;
  pointer-events: auto;
}
.carousel-arrows--bottomCenter {
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
}
.carousel-arrows--middleSides {
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  justify-content: space-between;
  padding: 0 4px;
  align-items: center;
}
.carousel-arrows--middleVertical {
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
  .carousel-arrow-btn {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    pointer-events: auto;
  }
  .carousel-arrow-btn:first-child {
    top: 6px;
  }
  .carousel-arrow-btn:last-child {
    bottom: 6px;
  }
}
.carousel-arrow-btn {
  border: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  opacity: 0.55;
  transition: opacity 0.15s ease;
  &:hover {
    opacity: 0.85;
  }
  i {
    font-size: 14px;
    font-weight: normal;
    color: inherit;
  }
}
</style>
